import { DOCUMENT, Location } from '@angular/common';
import { DestroyRef, Inject, Injectable, InjectionToken, Renderer2, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HammerGestureConfig } from '@angular/platform-browser';

import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { WINDOW } from '@frontend/vanilla/core';
import fastdom from 'fastdom';
import { TouchMouseInput } from 'hammerjs';
import { animationFrames, exhaustMap, filter, fromEvent, take, tap } from 'rxjs';

import { GestureOptions, PopupAction } from '../common/modal-window.component';
import { ModalAnimationHelperService } from './modal-animation-helper.service';
import { OpenedModalsStorageService } from './opened-modals-storage.service';

export const HORIZONTAL_GESTURE_CONFIG = new InjectionToken<HammerGestureConfig>('ModalGestureHorizontalConfig');

@Injectable()
export class ModalGestureHorizontalConfig extends HammerGestureConfig {
    override overrides = {
        pan: {
            direction: Hammer.DIRECTION_HORIZONTAL,
        },
    };

    override options = {
        inputClass: TouchMouseInput,
        touchAction: 'pan-x',
    };
}

@Injectable()
export class ModalHorizontalGestureService {
    private readonly _window = inject(WINDOW);

    private modalWindow: HTMLElement;
    private isDragging = false;
    private isAnimating = false;
    private currentX = 0;
    private readonly closingVelocity = 0.5; //velocity expressed in px/ms.
    private currentBackdropOpacity = this.animationHelper.maxBackdropOpacity;
    private currentBackgroundPanValue = this.animationHelper.maxBackgroundPanValue;
    private backgroundWindow: HTMLElement | null;
    private modalBody: HTMLElement | null;
    private readonly destroyRef = inject(DestroyRef);
    private previousUrl: string | undefined;

    private get modalBackdrop(): HTMLElement {
        const backdrops = this.document.querySelectorAll<HTMLElement>('ms-modal-backdrop');
        return backdrops[backdrops.length - 1];
    }

    constructor(
        @Inject(HORIZONTAL_GESTURE_CONFIG) private hammerGestureConfig: HammerGestureConfig,
        @Inject(DOCUMENT) private readonly document: Document,
        private openedModals: OpenedModalsStorageService,
        private renderer: Renderer2,
        private animationHelper: ModalAnimationHelperService,
        private location: Location,
        private routerEventsService: RouterEventsService,
    ) {}

    addGestures(modalWindow: unknown, { enabled }: GestureOptions): void {
        if (!enabled || !(modalWindow instanceof HTMLElement)) {
            return;
        }

        this.previousUrl = this.trySetPreviousUrl(this.routerEventsService.urlHistory);
        this.backgroundWindow = this.document.querySelector<HTMLElement>('.app-root');
        this.modalWindow = modalWindow;
        this.modalBody = this.document.querySelector<HTMLElement>('.modal-body');
        const duration = 400;
        this.animationHelper.animateBackdrop(0, this.animationHelper.maxBackdropOpacity, duration);

        this.animateBackground(0, this.animationHelper.maxBackgroundPanValue, duration);
        this.modalWindow
            .animate([{ transform: 'translateX(100%)' }, { transform: 'translateX(0%)' }], {
                duration,
                iterations: 1,
                easing: 'cubic-bezier(0, 0.2, 0.2, 1)',
            })
            .finished.then(() => {
                this.handlePanEvents(this.modalWindow);
            });
    }

    private animateBackground(fromPosition: number, toPosition: number, duration: number): void {
        this.animationHelper.animateBackground({ fromPosition, toPosition, duration }).subscribe(() => {
            this.renderer.removeStyle(this.backgroundWindow, 'will-change');
            this.renderer.removeStyle(this.backgroundWindow, 'position');
            this.renderer.removeStyle(this.backgroundWindow, 'display');
            this.renderer.removeStyle(this.backgroundWindow, 'right');
            this.renderer.removeStyle(this.modalBody, 'overflow-y');
        });
    }

    private handlePanEvents(panElement: HTMLElement): void {
        const hammer = this.hammerGestureConfig.buildHammer(panElement);
        fromEvent<HammerInput>(hammer, 'panstart')
            .pipe(
                filter(
                    (pan) =>
                        !this.isDragging &&
                        !this.isAnimating &&
                        pan.direction === Hammer.DIRECTION_RIGHT &&
                        Math.round((pan.srcEvent as TouchEvent).touches[0].clientX) <= 50,
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() =>
                fastdom.mutate(() => {
                    this.handlePanstart();
                }),
            );

        fromEvent<HammerInput>(hammer, 'panmove')
            .pipe(
                filter((pan) => this.isDragging && pan.deltaX > 0),
                takeUntilDestroyed(this.destroyRef),
                exhaustMap((pan) =>
                    animationFrames().pipe(
                        take(1),
                        tap(() =>
                            fastdom.mutate(() => {
                                this.handlePanmove(pan);
                            }),
                        ),
                    ),
                ),
            )
            .subscribe();

        fromEvent<HammerInput>(hammer, 'panend')
            .pipe(
                filter(() => this.isDragging),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((pan) =>
                fastdom.mutate(() => {
                    this.handlePanend(pan.velocity);
                }),
            );
    }

    private handlePanstart(): void {
        this.isDragging = true;
        this.renderer.setStyle(this.modalWindow, 'will-change', 'left');
        this.renderer.setStyle(this.modalWindow, 'min-width', '100vw');
        this.renderer.setStyle(this.modalBody, 'overflow-y', 'hidden');
    }

    private handlePanend(velocity: number): void {
        if (!this.isDragging) {
            return;
        }

        this.isDragging = false;
        this.isAnimating = true;
        const animationTime = this.animationHelper.calculateAnimationTime({ currentX: this.currentX });
        const shouldClose = this.currentX > this._window.screen.width * this.animationHelper.closingRatioX;

        if (velocity >= this.closingVelocity) {
            this.closeWithVelocity(shouldClose, velocity, animationTime);
        } else {
            if (shouldClose) {
                this.animateClose(animationTime);
                this.animateBackground(this.currentBackgroundPanValue, 0, this.animationHelper.animationDurationX);
            } else {
                this.animateModal(false, this.currentX, animationTime);
                this.animateBackground(
                    this.currentBackgroundPanValue,
                    this.animationHelper.maxBackgroundPanValue,
                    this.animationHelper.animationDurationX,
                );
            }
        }
    }

    private closeWithVelocity(shouldClose: boolean, velocity: number, animationTime: number): void {
        const velocityAnimationTime = (this._window.innerWidth - this.currentX) / velocity;

        if (!shouldClose || (velocityAnimationTime > 0 && velocityAnimationTime < animationTime)) {
            this.animateClose(velocityAnimationTime);
            this.animateBackground(this.currentBackgroundPanValue, 0, velocityAnimationTime);
        } else {
            this.animateClose(animationTime);
            this.animateBackground(this.currentBackgroundPanValue, 0, animationTime);
        }
    }

    private animateClose(animationTime: number): void {
        this.setModalLeft(this._window.innerWidth);
        this.animateModal(true, this.currentX, animationTime, true);
    }

    private animateModal(shouldSlideOut: boolean, currentX: number, duration: number, shouldClose?: boolean): void {
        this.animationHelper.animateModal(shouldSlideOut, { currentX }, duration).subscribe(() => {
            this.renderer.removeAttribute(this.modalWindow, 'style');
            this.isAnimating = false;

            if (shouldClose) {
                this.panClose();
            }
        });
    }

    private handlePanmove(event: HammerInput): void {
        this.currentX = event.deltaX;

        if (this.currentX <= this._window.screen.width) {
            this.setModalLeft(this.currentX);
        } else {
            this.panClose();
        }
    }

    private setModalLeft(left = 0): void {
        if (left < 0) {
            return;
        }

        if (!this.isAnimating) {
            this.setBackgroundOffset(left);
            this.renderer.setStyle(this.modalWindow, 'left', `${left}px`);
        }
    }

    private panClose(): void {
        this.openedModals.activeModal?.close({ closeAction: PopupAction.CloseByPan });
        if (this.previousUrl) {
            this.location.replaceState(this.previousUrl);
        }
    }

    private trySetPreviousUrl(urlHistory?: string[]): string | undefined {
        const nonEdpUrls = urlHistory?.filter((url) => !url.includes('/events'));
        if (nonEdpUrls && nonEdpUrls.length !== 0) {
            return nonEdpUrls[nonEdpUrls.length - 1];
        }
        return undefined;
    }

    private setBackgroundOffset(currentX: number): void {
        if (this.openedModals.count > 1) {
            return;
        }

        this.currentBackgroundPanValue =
            this.animationHelper.maxBackgroundPanValue -
            Math.round(this.animationHelper.maxBackgroundPanValue * (currentX / this._window.screen.width));
        this.renderer.setStyle(this.backgroundWindow, 'display', 'block');
        this.renderer.setStyle(this.backgroundWindow, 'position', 'relative');
        this.renderer.setStyle(this.backgroundWindow, 'right', `${this.currentBackgroundPanValue}px`);

        this.currentBackdropOpacity =
            this.animationHelper.maxBackdropOpacity -
            Number((this.animationHelper.maxBackdropOpacity * (currentX / this._window.screen.width)).toFixed(2));
        this.renderer.setStyle(this.modalBackdrop, 'opacity', `${this.currentBackdropOpacity}`);
    }
}
